import React, { useState } from 'react';
import { useTranslation } from "react-i18next";
import { Input } from '../../ui';
import { ColorSelect } from '../portal/ColorSelect';
import LogoUploader from '../portal/LogoUploader';
import { useUIStore } from '../../../store/uiStore';
import { PremiumBrandingAlert } from '../portal/PremiumBrandingAlert';

interface HelpCenterBrandingProps {
  settings?: {
    themeColor?: string;
    logoUrl?: string;
    defaultLogoUrl?: string;
    companyUrl?: string;
  };
  isProCloudPlan: boolean;
  onUpdate: (settings: {
    themeColor?: string;
    logoUrl?: string;
    companyUrl?: string;
  }, logoFile?: File | null, removeFile?: boolean) => Promise<void>;
}

function isValidHttpUrl(str: string | undefined) {
  if (!str) {
    return true;
  }

  let url;

  try {
    url = new URL(str);
  } catch {
    return false;
  }

  return url.protocol === "http:" || url.protocol === "https:";
}

export const HelpCenterBranding: React.FC<HelpCenterBrandingProps> = ({ 
  settings, 
  isProCloudPlan,
  onUpdate 
}) => {
  const { t } = useTranslation();
  const { setIsSaving } = useUIStore();
  const [localSettings, setLocalSettings] = useState({
    themeColor: settings?.themeColor || '',
    companyUrl: settings?.companyUrl || '',
    logoUrl: settings?.logoUrl || '',
    defaultLogoUrl: settings?.defaultLogoUrl || '',
  });

  const [companyUrlError, setCompanyUrlError] = useState('');
  // Last value sent to the server, so blur without changes doesn't re-save
  const [savedCompanyUrl, setSavedCompanyUrl] = useState(settings?.companyUrl || '');

  const updateSettings = async (
    updates: Partial<typeof localSettings>, 
    logoFile?: File | null, 
    removeFile?: boolean
  ) => {
    const newSettings = { ...localSettings, ...updates };
    setLocalSettings(newSettings);
    
    try {
      setIsSaving(true);
      await onUpdate({
        themeColor: newSettings.themeColor,
        logoUrl: newSettings.logoUrl,
        // Never send an unsaved (possibly invalid) URL along with other changes
        companyUrl: savedCompanyUrl,
      }, logoFile, removeFile);
    } catch (error) {
      console.error('Error updating help center branding:', error);
    } finally {
      setTimeout(() => setIsSaving(false), 400);
    }
  };

  const handleThemeColorChange = (themeColor: string) => {
    updateSettings({ themeColor });
  };

  const handleCompanyUrlChange = (companyUrl: string) => {
    setLocalSettings((prev) => ({ ...prev, companyUrl }));
    if (companyUrlError && isValidHttpUrl(companyUrl.trim())) {
      setCompanyUrlError('');
    }
  };

  const saveCompanyUrl = async () => {
    const companyUrl = localSettings.companyUrl.trim();
    if (!isValidHttpUrl(companyUrl)) {
      setCompanyUrlError(t('helpCenter.branding.companyUrlError'));
      return;
    }
    setCompanyUrlError('');
    if (companyUrl === savedCompanyUrl) {
      return;
    }
    try {
      setIsSaving(true);
      await onUpdate({
        themeColor: localSettings.themeColor,
        logoUrl: localSettings.logoUrl,
        companyUrl,
      });
      setSavedCompanyUrl(companyUrl);
      setLocalSettings((prev) => ({ ...prev, companyUrl }));
    } catch (error) {
      console.error('Error updating help center website URL:', error);
    } finally {
      setTimeout(() => setIsSaving(false), 400);
    }
  };

  const handleRemoveLogo = () => {
    updateSettings({ logoUrl: '' }, null, true);
  };

  const handleLogoUpload = (file: File) => {
    // Create a preview URL for immediate display
    const url = URL.createObjectURL(file);
    updateSettings({ logoUrl: url }, file, false);
  };

  return (
    <div className="space-y-6">
      {!isProCloudPlan && (
        <PremiumBrandingAlert context="help-center" />
      )}
      <div
        className={`${
          !isProCloudPlan ? "opacity-50 pointer-events-none" : ""
        }`}
      >
        <div className="grid gap-4 gap-y-2 text-sm grid-cols-1 md:grid-cols-5">
        <div className="md:col-span-6">
          <div className="space-y-2 flex flex-wrap flex-row items-center justify-between mt-5">
            <div className="space-y-0.5">
              <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                {t('helpCenter.branding.colorSchemeLabel')}
              </label>
              <p className="text-[0.8rem] text-muted-foreground">
                {t('helpCenter.branding.colorSchemeDescription')}
              </p>
            </div>
            <ColorSelect
              value={localSettings.themeColor}
              onChange={handleThemeColorChange}
            />
          </div>

          <div className="space-y-2 flex flex-wrap flex-row items-center justify-between mt-5">
            <div className="space-y-0.5">
              <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                {t('helpCenter.branding.logoLabel')}
              </label>
              <p className="text-[0.8rem] text-muted-foreground">
                {t('helpCenter.branding.logoDescription')}
              </p>
            </div>
            <div className="w-[250px]">
              <LogoUploader
                value={localSettings.logoUrl}
                onChange={(url) => updateSettings({ logoUrl: url })}
                onRemove={handleRemoveLogo}
                onUploadFile={handleLogoUpload}
                defaultLogo={localSettings.defaultLogoUrl}
                maxSize={2}
                maxHeight={1000}
                maxWidth={1000}
                acceptedTypes={["image/jpeg", "image/png"]}
              />
            </div>
          </div>

          <div className="space-y-2 flex flex-row items-center justify-between gap-4 mt-5">
            <div className="space-y-0.5 flex-1 min-w-0">
              <label
                htmlFor="help-center-company-url"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                {t('helpCenter.branding.websiteUrlLabel')}
              </label>
              <p className="text-[0.8rem] text-muted-foreground text-wrap mr-2">
                {t('helpCenter.branding.websiteUrlDescription')}
              </p>
              {companyUrlError && (
                <p className="text-sm text-red-500">{companyUrlError}</p>
              )}
            </div>
            <Input
              id="help-center-company-url"
              type="url"
              value={localSettings.companyUrl}
              onChange={(e) => handleCompanyUrlChange(e.target.value)}
              onBlur={saveCompanyUrl}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.currentTarget.blur();
                }
              }}
              placeholder="https://example.com"
              maxLength={250}
              className="h-8 text-gray-500 text-sm border rounded px-2 !w-[250px] shrink-0 ml-auto"
            />
          </div>
        </div>
        </div>
      </div>
    </div>
  );
};

